import { useEffect, useState, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { LockKeyhole, LogOut, RefreshCw, ClipboardList, Download, ArrowLeft } from "lucide-react";
import { getAdminMe, adminLogout, startAdminLogin } from "@/lib/adminAuth";
import { fetchAdminPrintInterest, adminPrintInterestCsvUrl } from "@/lib/adminPrintInterest";
import { fetchAdminReleaseInterest } from "@/lib/adminReleaseInterest";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function BucketList({ title, counts }) {
  const entries = Object.entries(counts || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div className="rounded-sm border border-white/10 bg-white/[0.03] p-5">
      <p className="text-[11px] uppercase tracking-[0.16em] text-white/50 font-semibold mb-3">{title}</p>
      {entries.length === 0 ? (
        <p className="text-white/30 text-sm">No data yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {entries.map(([key, count]) => (
            <li key={key} className="flex items-center justify-between text-[13px]">
              <span className="text-white/75">{key}</span>
              <span className="text-white/40 font-mono">{count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const TABS = [
  { id: "all", label: "All Interest" },
  { id: "print", label: "Print Editions" },
  { id: "release", label: "Series Releases" },
];

/** Owner-only interest report — combines Print Edition interest (physical
 * books, Command 02) and Series Release interest (unreleased Level 1/2,
 * Command 02A) side by side. Reuses the existing Emergent-managed Google
 * Auth session (see AdminOrders.jsx); no separate authentication system. */
export default function AdminBookInterest() {
  const location = useLocation();
  const [authChecking, setAuthChecking] = useState(location.state?.user ? false : true);
  const [user, setUser] = useState(location.state?.user || null);

  const [tab, setTab] = useState("all");
  const [printData, setPrintData] = useState(null);
  const [releaseData, setReleaseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (location.state?.user) return;
    (async () => {
      const me = await getAdminMe();
      setUser(me);
      setAuthChecking(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [print, release] = await Promise.all([
        fetchAdminPrintInterest(200),
        fetchAdminReleaseInterest(200),
      ]);
      setPrintData(print);
      setReleaseData(release);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const handleLogout = async () => {
    await adminLogout();
    setUser(null);
  };

  if (authChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D1B2A]">
        <p className="text-white/50 text-sm tracking-[0.14em] uppercase">Checking access…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div data-testid="admin-book-interest-login-gate" className="min-h-screen flex items-center justify-center bg-[#0D1B2A] px-6">
        <div className="max-w-sm w-full text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-6">
            <LockKeyhole size={22} className="text-[#F57F17]" />
          </div>
          <h1 className="text-white font-serif text-2xl mb-2">LifeIQ Admin</h1>
          <p className="text-white/50 text-sm mb-8">Sign in with the owner Google account to view reader interest.</p>
          <button
            type="button"
            data-testid="admin-book-interest-google-login-btn"
            onClick={startAdminLogin}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 text-[13px] font-semibold tracking-wide uppercase rounded-sm bg-[#F57F17] text-white hover:bg-[#D86B0F] transition-colors"
          >
            Sign in with Google
          </button>
          <p className="text-white/30 text-xs mt-6">You'll land on Order History first — use the nav link from there.</p>
        </div>
      </div>
    );
  }

  const totalAll = (printData?.total ?? 0) + (releaseData?.total ?? 0);
  const mergedRecent = [
    ...(printData?.recent || []).map((r) => ({ ...r, _kind: "Print" })),
    ...(releaseData?.recent || []).map((r) => ({ ...r, _kind: "Release" })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const activeTotal = tab === "print" ? printData?.total : tab === "release" ? releaseData?.total : totalAll;
  const activeLabel = tab === "print" ? "print-interest submissions" : tab === "release" ? "series-release submissions" : "combined interest submissions";
  const activeRecent = tab === "print" ? (printData?.recent || []) : tab === "release" ? (releaseData?.recent || []) : mergedRecent;

  return (
    <div data-testid="admin-book-interest-page" className="min-h-screen bg-[#0D1B2A] px-6 py-10">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/admin" data-testid="admin-nav-orders-link" className="inline-flex items-center gap-1.5 text-white/40 hover:text-white/70 text-[11px] uppercase tracking-wide transition-colors mb-2">
              <ArrowLeft size={12} /> Order History
            </Link>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#F57F17] font-semibold mb-1">LifeIQ Admin</p>
            <h1 className="text-white font-serif text-2xl">Reader Interest</h1>
          </div>
          <div className="flex items-center gap-4">
            <span data-testid="admin-book-interest-user-email" className="text-white/50 text-sm">{user.email}</span>
            <button
              type="button"
              data-testid="admin-book-interest-logout-btn"
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-[12px] uppercase tracking-wide transition-colors"
            >
              <LogOut size={14} /> Log out
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-6">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              data-testid={`admin-book-interest-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.1em] rounded-full border transition-colors ${
                tab === t.id ? "bg-[#F57F17] border-[#F57F17] text-white" : "border-white/15 text-white/60 hover:text-white hover:border-white/35"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between mb-6">
          <div data-testid="admin-book-interest-total" className="inline-flex items-center gap-2 text-white">
            <ClipboardList size={16} className="text-[#F57F17]" />
            <span className="font-serif text-xl">{activeTotal ?? "—"}</span>
            <span className="text-white/50 text-sm">{activeLabel}</span>
          </div>
          <div className="flex items-center gap-3">
            {tab !== "release" && (
              <a
                href={adminPrintInterestCsvUrl()}
                data-testid="admin-book-interest-csv-export"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold uppercase tracking-wide rounded-sm border border-white/15 text-white/70 hover:text-white transition-colors"
              >
                <Download size={13} /> Export Print CSV
              </a>
            )}
            <button
              type="button"
              data-testid="admin-book-interest-refresh-btn"
              onClick={load}
              className="px-3 py-2 rounded-sm border border-white/15 text-white/60 hover:text-white transition-colors"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {error && <p data-testid="admin-book-interest-error" className="text-red-400 text-sm mb-4">{error}</p>}

        {tab !== "release" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
            <BucketList title="By Series" counts={printData?.by_series} />
            <BucketList title="By City" counts={printData?.by_city} />
            <BucketList title="By Quantity Band" counts={printData?.by_quantity_band} />
            <BucketList title="By Visitor Type" counts={printData?.by_visitor_type} />
            <BucketList title="By Source Page" counts={printData?.by_source} />
            <BucketList title="By Product / Book" counts={printData?.by_product} />
          </div>
        )}

        {tab === "release" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
            <BucketList title="By Series" counts={releaseData?.by_series} />
            <BucketList title="By Source Page" counts={releaseData?.by_source} />
          </div>
        )}

        <p className="text-[11px] uppercase tracking-[0.16em] text-white/50 font-semibold mb-3">Recent submissions</p>
        <div className="rounded-sm border border-white/10 bg-white/[0.03] overflow-hidden overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                {tab === "all" && <TableHead className="text-white/50">Type</TableHead>}
                <TableHead className="text-white/50">Name</TableHead>
                <TableHead className="text-white/50">Contact</TableHead>
                <TableHead className="text-white/50">Series</TableHead>
                {tab === "print" && <TableHead className="text-white/50">City</TableHead>}
                {tab === "print" && <TableHead className="text-white/50">Qty</TableHead>}
                <TableHead className="text-white/50">Source</TableHead>
                <TableHead className="text-white/50">Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activeRecent.length === 0 && !loading && (
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableCell colSpan={7} className="text-white/40 text-center py-10">
                    No submissions yet.
                  </TableCell>
                </TableRow>
              )}
              {activeRecent.map((r) => (
                <TableRow key={r.interest_id} data-testid={`admin-book-interest-row-${r.interest_id}`} className="border-white/10 hover:bg-white/[0.04]">
                  {tab === "all" && <TableCell className="text-white/60 text-xs">{r._kind}</TableCell>}
                  <TableCell className="text-white/80">{r.name}</TableCell>
                  <TableCell className="text-white/60 text-xs">{r.email || r.whatsapp || "—"}</TableCell>
                  <TableCell className="text-white/60 text-xs">{r.series_ids ? r.series_ids.join(", ") : (r.series_id || "—")}</TableCell>
                  {tab === "print" && <TableCell className="text-white/60 text-xs">{r.city || "—"}</TableCell>}
                  {tab === "print" && <TableCell className="text-white/60 text-xs">{r.quantity_band || "—"}</TableCell>}
                  <TableCell className="text-white/60 text-xs">{r.source_page || "—"}</TableCell>
                  <TableCell className="text-white/60 text-xs">{formatDate(r.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
